import { useEffect, useId, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api.js';

// Google Places Autocomplete (New), behind our own /api/v1/places endpoints so the API key
// never reaches the browser (batch item 2). Falls back to a plain text field — with a short
// warning — if Places is down or not configured, so typing a city always keeps working.

export interface ResolvedPlace {
  placeId: string;
  formattedAddress: string;
  city: string;
  state: string;
  country: string;
  countryCode: string;
  lat: number;
  lng: number;
}

interface Suggestion {
  placeId: string;
  text: string;
}

function newSessionToken(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${String(Date.now())}-${Math.random().toString(36).slice(2)}`;
}

export function LocationAutocomplete({
  label,
  value,
  onChange,
  onResolve,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (text: string) => void;
  // Called with the resolved place on selection, or null if the person keeps typing freely
  // instead of picking a suggestion (the plain-text fallback).
  onResolve: (place: ResolvedPlace | null) => void;
  placeholder?: string;
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const sessionToken = useRef(newSessionToken());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listId = useId();

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  function search(text: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.trim().length < 2 || unavailable) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      void api<{ suggestions: Suggestion[] }>('POST', '/api/v1/places/autocomplete', {
        input: text.trim(),
        sessionToken: sessionToken.current,
      })
        .then((r) => {
          setSuggestions(r.suggestions);
          setOpen(r.suggestions.length > 0);
        })
        .catch((e: unknown) => {
          if (e instanceof ApiError && e.status === 503) setUnavailable(true);
          setSuggestions([]);
        });
    }, 250);
  }

  async function select(s: Suggestion) {
    onChange(s.text);
    setOpen(false);
    setSuggestions([]);
    try {
      const { place } = await api<{ place: ResolvedPlace }>('POST', '/api/v1/places/details', {
        placeId: s.placeId,
        sessionToken: sessionToken.current,
      });
      onResolve(place);
      sessionToken.current = newSessionToken(); // a session ends once a place is picked
    } catch {
      onResolve(null);
    }
  }

  return (
    <div className="location-autocomplete">
      <label>
        {label}
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(e) => {
            onChange(e.target.value);
            onResolve(null); // typing invalidates any previously resolved place
            search(e.target.value);
          }}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
          onBlur={() => {
            setTimeout(() => {
              setOpen(false);
            }, 150);
          }}
        />
      </label>
      {open && suggestions.length > 0 && (
        <ul id={listId} role="listbox" className="location-suggestions">
          {suggestions.map((s) => (
            <li key={s.placeId} role="option" aria-selected={false}>
              <button type="button" onMouseDown={() => void select(s)}>
                {s.text}
              </button>
            </li>
          ))}
        </ul>
      )}
      {unavailable && (
        <p className="tiny muted">Location search is unavailable right now — type it in.</p>
      )}
    </div>
  );
}
